import { queryOptions, useQuery } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import { hasPlatformSchemas, setPlatformSchemas } from '../model/platform-structure';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import { platformFieldSchemasSchema } from './schemas';

/**
 * Which fields each platform has, what it calls its levels, and the allowed values.
 *
 * Static per deployment, so it is cached hard: refetching it while a reviewer works
 * through a plan would buy nothing and risks columns moving under them.
 */
export const platformFieldSchemasQueryOptions = () =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.platformSchemas(),
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/platform-field-schemas',
        signal,
      });

      return parseResponse(platformFieldSchemasSchema, res);
    },
  });

/**
 * Loads the schemas and publishes them to the module-level registry the pure
 * helpers read from.
 *
 * `isReady` gates the review screen: rendering a table before the schemas land
 * would draw it with no columns and then reflow, which reads as a bug.
 */
export function usePlatformSchemas(): { isReady: boolean; isError: boolean } {
  const { data, isError } = useQuery(platformFieldSchemasQueryOptions());
  const items = data?.items;

  // Applied during render, not in an effect: the helpers are read by the very render
  // that first sees the data, and an effect would leave that render with an empty
  // registry and nothing to re-trigger it. `setPlatformSchemas` no-ops on a repeat
  // of the same array, so this stays cheap.
  if (Array.isArray(items)) setPlatformSchemas(items);

  return { isReady: Array.isArray(items) && hasPlatformSchemas(), isError };
}
